import { useEffect, useRef, useState } from 'react';
import { useBahasa } from '../i18n';

/**
 * Konfirmasi hapus — **satu dialog untuk seluruh aplikasi** (§G17).
 *
 * Sampai 7 September 2026 tiap tombol Delete punya pagarnya sendiri: kamera
 * memakai dialog ini (dulu tinggal di `CamerasPage`), sebagian halaman memakai
 * `confirm()` bawaan browser, dan Custom Dashboards tidak memakai apa pun sama
 * sekali — satu klik, dan susunan yang dirakit sendiri hilang tanpa sempat
 * ditanya. Yang paling mahal dari keadaan itu bukan halaman yang paling
 * longgar, melainkan **ketidakkonsistenannya**: begitu ada satu Delete yang
 * cukup satu klik, tombol Delete berhenti berarti "berhenti dulu" di mana pun.
 *
 * Dua pagar, untuk dua kesalahan yang berbeda:
 *
 * - **Nama diketik ulang** menangkap kesalahan orang yang memang berhak.
 *   Mengetik ulang bukan ritual — ia memaksa mata membaca nama yang tertulis di
 *   layar dan mencocokkannya dengan yang ada di kepala, dan itulah yang
 *   menangkap "saya kira saya sedang menghapus yang satunya". Polanya diambil
 *   dari dialog hapus dataset TrueNAS.
 * - **Password** menangkap orang yang tidak berhak tapi kebetulan duduk di
 *   depan laptop yang belum terkunci. Mengetik ulang nama yang tertulis
 *   besar-besar di layar bisa dilakukan siapa saja yang ada di ruangan itu.
 *
 * Keduanya diperiksa **ulang di server** (`auth/konfirmasiHapus.ts`); yang di
 * sini semata-mata supaya orangnya tahu apa yang diminta sebelum menekan
 * tombolnya. Pagar yang cuma hidup di dialog bukan pagar, ia saran.
 */

export interface KonfirmasiHapus {
  confirmName: string;
  password: string;
}

export function DialogHapus({
  /** Nama yang harus diketik ulang. Wajib **sama persis** dengan yang di layar. */
  nama,
  judul,
  /** Satu kalimat: apa yang hilang, dan apakah ia bisa kembali. */
  keterangan,
  onBatal,
  onHapus,
}: {
  nama: string;
  judul: string;
  keterangan: React.ReactNode;
  onBatal: () => void;
  onHapus: (konfirmasi: KonfirmasiHapus) => Promise<void>;
}) {
  const { t } = useBahasa();
  const [ketikan, setKetikan] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [disentuh, setDisentuh] = useState(false);
  /** Pesan dari server — "password salah" hampir selalu yang muncul di sini. */
  const [gagal, setGagal] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const cocok = ketikan.trim() === nama.trim();
  const siap = cocok && password !== '';

  useEffect(() => {
    inputRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // Dihentikan di sini supaya Escape yang dimaksudkan menutup dialog tidak
      // ikut menutup layar penuh atau panel di belakangnya.
      e.stopPropagation();
      onBatal();
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onBatal]);

  async function kirim(e: React.FormEvent) {
    e.preventDefault();
    setDisentuh(true);
    if (!siap || busy) return;
    setBusy(true);
    setGagal(null);
    try {
      await onHapus({ confirmName: ketikan.trim(), password });
    } catch (err) {
      // Password dikosongkan setiap kali ditolak: kotak yang masih berisi
      // ketikan yang baru saja salah mengundang orang menekan Delete lagi
      // dengan isi yang persis sama.
      setPassword('');
      setGagal((err as Error).message);
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="dialog-hapus-judul"
    >
      <form
        onSubmit={(e) => void kirim(e)}
        className="w-full max-w-md rounded-lg border border-line bg-surface p-5 shadow-xl"
      >
        <h2 id="dialog-hapus-judul" className="text-lg">
          {judul}
        </h2>

        <p className="mt-2 text-sm text-muted">{keterangan}</p>

        <label className="mt-4 block text-sm">
          {t('Enter')} <strong className="text-[color:var(--color-text)]">{nama}</strong>{' '}
          {t('below to confirm.')}
          <input
            ref={inputRef}
            value={ketikan}
            onChange={(e) => setKetikan(e.target.value)}
            autoComplete="off"
            spellCheck={false}
            aria-invalid={disentuh && !cocok}
            className="mt-1 min-h-tap w-full rounded-md border border-line bg-surface px-3 text-sm"
          />
        </label>

        {disentuh && !cocok && (
          <p role="alert" className="mt-1 text-xs text-red-600">
            {t('The name must match exactly.')}
          </p>
        )}

        <label className="mt-3 block text-sm">
          {t('Enter your password to confirm.')}
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            aria-invalid={disentuh && password === ''}
            className="mt-1 min-h-tap w-full rounded-md border border-line bg-surface px-3 text-sm"
          />
        </label>

        {disentuh && cocok && password === '' && (
          <p role="alert" className="mt-1 text-xs text-red-600">
            {t('Your password is required.')}
          </p>
        )}

        {gagal && (
          <p role="alert" className="mt-2 text-xs text-red-600">
            {gagal}
          </p>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onBatal}
            className="inline-flex min-h-tap items-center rounded-md border border-line px-4 text-sm transition-[background-color,scale] duration-150 hover:bg-subtle active:scale-[0.98]"
          >
            {t('Cancel')}
          </button>
          <button
            type="submit"
            disabled={!siap || busy}
            className="min-h-tap rounded-md bg-red-600 px-4 text-sm font-semibold text-white transition-[background-color,scale] duration-150 active:scale-[0.98] disabled:opacity-40"
          >
            {busy ? t('Deleting…') : t('Delete')}
          </button>
        </div>
      </form>
    </div>
  );
}
